import { getHeaderTitle } from '@react-navigation/elements'
import { RouteProp } from '@react-navigation/native'
import { NativeStackNavigationOptions } from '@react-navigation/native-stack'
import { router } from 'expo-router'
import { useEffect } from 'react'
import { Platform, View } from 'react-native'
import { Appbar } from 'react-native-paper'
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useUnistyles } from 'react-native-unistyles'

import DesktopSearchBar from '@/components/navigation/DesktopSearchBar'
import Header from '@/components/navigation/header/Header'
import UniButton from '@/components/unistyles/UniButton'
import UniIconButton from '@/components/unistyles/UniIconButton'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'

const FosterHeader = ({
  route,
  title,
  options,
}: {
  route: RouteProp<Record<string, object | undefined>, string>
  title?: string
  options: NativeStackNavigationOptions
}) => {
  const { theme } = useUnistyles()
  const bulkSelect = useAppStore.use.bulkSelect()
  const resetBulkSelect = useAppStore.use.resetBulkSelect()

  // Reanimated shared value for animation
  const animation = useSharedValue(0)

  // Update animation value based on selection state
  const isSelectionActive =
    bulkSelect.selection.items.length > 0 && route.name === 'fosters'

  useEffect(() => {
    animation.value = withTiming(isSelectionActive ? 1 : 0, { duration: 200 })
  }, [animation, isSelectionActive])

  // Animated style for header background color
  const animatedStyle = useAnimatedStyle(() => {
    const backgroundColor = interpolateColor(
      animation.value,
      [0, 1],
      [theme.colors.surface, theme.colors.secondaryContainer]
    )

    return { backgroundColor }
  })

  const headerRight = options.headerRight?.({ canGoBack: true })

  return (
    <Animated.View style={animatedStyle}>
      <Header
        options={options}
        replaceContentWithChildren={true}
        route={route}
        title={title}
        transparent
      >
        {isSelectionActive ? (
          <>
            <UniIconButton
              icon="close"
              iconColor={theme.colors.onSecondaryContainer}
              onPress={() => resetBulkSelect()}
              testID="clear-selection-button"
            />
            <Appbar.Content
              title={`${bulkSelect.selection.items.length} Selected`}
              titleMaxFontSizeMultiplier={1.5}
            />
            <UniButton
              onPress={() => {
                router.push({
                  pathname: buildOrgPath('/fosters/bulk'),
                  params: { ids: bulkSelect.selection.items },
                })
              }}
            >
              Edit All
            </UniButton>
          </>
        ) : (
          <>
            <View style={{ flex: 1, flexDirection: 'row' }}>
              <Appbar.Content
                style={{ flex: 1 }}
                title={title || getHeaderTitle(options, route.name)}
                titleMaxFontSizeMultiplier={1.1}
              />
            </View>

            {options.headerRight && (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <>
                  {Platform.OS === 'web' && (
                    <View
                      style={{
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                      }}
                    >
                      <DesktopSearchBar />
                    </View>
                  )}
                </>
                {headerRight}
              </View>
            )}
          </>
        )}
      </Header>
    </Animated.View>
  )
}

export default FosterHeader
